import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { Rocket, Target, BrainCircuit, Settings2, Maximize, ChevronRight, RefreshCw } from 'lucide-react';
interface SlideLoopProps {
  onContinue: () => void;
}
// Slide 8 — The Snakker Flywheel
// Linear 5-stage flow with a clear loop-back to the next campaign brief.
export function SlideLoop({
  onContinue
}: SlideLoopProps) {
  const steps = [{
    id: '01',
    title: 'Launch',
    icon: <Rocket className="w-5 h-5" data-id="element-338" />,
    desc: 'Campaign goes live across selected countries and channels'
  }, {
    id: '02',
    title: 'Capture',
    icon: <Target className="w-5 h-5" data-id="element-339" />,
    desc: 'HCPs answer, click, scan and choose content paths'
  }, {
    id: '03',
    title: 'Interpret',
    icon: <BrainCircuit className="w-5 h-5" data-id="element-340" />,
    desc: 'Knowledge gaps, country differences, message preferences'
  }, {
    id: '04',
    title: 'Optimize',
    icon: <Settings2 className="w-5 h-5" data-id="element-341" />,
    desc: 'Next campaign refines message, CTA, channel and format'
  }, {
    id: '05',
    title: 'Scale',
    icon: <Maximize className="w-5 h-5" data-id="element-342" />,
    desc: 'Winning patterns reused across brands and markets'
  }];
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-16 md:py-12 max-w-7xl mx-auto" data-id="element-343">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-344">
        <span className="text-teal-400 text-xs uppercase tracking-widest font-medium" data-id="element-345">
          The Snakker Flywheel
        </span>
        <h2 className="text-3xl md:text-5xl font-serif text-white mt-3 mb-3" data-id="element-346">
          Every interaction becomes a{' '}
          <span className="text-teal-400" data-id="element-347">decision signal.</span>
        </h2>
        <p className="text-base text-white/60 max-w-3xl" data-id="element-348">
          Most campaigns end with a report. Snakker uses the report as the brief
          for the next campaign.
        </p>
      </motion.div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-8 mt-10 items-center" data-id="element-349">
        {/* LEFT — Linear stage flow + loop-back */}
        <div className="lg:col-span-8 space-y-5" data-id="element-350">
          <p className="text-white/40 text-[10px] uppercase tracking-widest font-medium" data-id="element-351">
            Five stages · one continuous loop
          </p>

          {/* Stages */}
          <div className="flex items-stretch gap-2" data-id="element-352">
            {steps.map((step, i) => <Fragment key={step.id} data-id="element-353">
                <motion.div initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.1
            }} className="flex-1 bg-navy-900/60 border border-navy-700 rounded-2xl p-4 flex flex-col" data-id="element-354">
                  <div className="flex items-center justify-between mb-3" data-id="element-355">
                    <div className="w-9 h-9 rounded-lg bg-teal-400/10 border border-teal-400/20 flex items-center justify-center text-teal-400" data-id="element-356">
                      {step.icon}
                    </div>
                    <span className="text-teal-400/70 font-mono text-[10px] tracking-widest" data-id="element-357">
                      {step.id}
                    </span>
                  </div>
                  <div className="text-white font-medium text-sm mb-1.5" data-id="element-358">
                    {step.title}
                  </div>
                  <div className="text-white/50 text-[11px] leading-snug" data-id="element-359">
                    {step.desc}
                  </div>
                </motion.div>

                {i < steps.length - 1 && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: i * 0.1 + 0.15
            }} className="flex items-center justify-center flex-shrink-0" data-id="element-360">
                    <ChevronRight className="w-4 h-4 text-teal-400/50" data-id="element-361" />
                  </motion.div>}
              </Fragment>)}
          </div>

          {/* Loop-back band */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.7
        }} className="relative flex items-center gap-3 bg-teal-400/5 border border-teal-400/30 border-dashed rounded-xl px-4 py-3" data-id="element-362">
            <RefreshCw className="w-4 h-4 text-teal-400 flex-shrink-0" data-id="element-363" />
            <p className="text-teal-300 text-sm" data-id="element-364">
              <span className="font-medium" data-id="element-365">Continuous loop —</span>{' '}
              <span className="text-white/70" data-id="element-366">
                Scale informs the next Launch. Every campaign sharpens the next
                brief.
              </span>
            </p>
          </motion.div>
        </div>

        {/* RIGHT — What Arnaud gets back */}
        <div className="lg:col-span-4 space-y-5" data-id="element-367">
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.6
        }} className="bg-navy-800/60 border border-teal-400/30 rounded-2xl p-6" data-id="element-368">
            <p className="text-white/40 text-[10px] uppercase tracking-widest font-medium mb-4" data-id="element-369">
              What Arnaud gets back
            </p>
            <ul className="space-y-3.5" data-id="element-370">
              {['What worked', 'Where it worked', 'Which HCP actions increased', 'What to change next', 'Which campaign to scale'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-371">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-400 mt-1.5 flex-shrink-0" data-id="element-372" />
                  <span className="text-white/80 text-sm" data-id="element-373">{item}</span>
                </li>)}
            </ul>
          </motion.div>

          <div className="border-l-2 border-teal-400/40 pl-4 py-1" data-id="element-374">
            <p className="text-white/70 text-sm italic" data-id="element-375">
              "The report is not the end. It is the next campaign's starting
              advantage."
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-end mt-4" data-id="element-376">
        <Button onClick={onContinue} data-id="element-377">See the proof →</Button>
      </div>
    </div>;
}